import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  CopyButton,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { CheckIcon, CopyIcon, FileArrowUpIcon, HashIcon, TextAaIcon } from '@phosphor-icons/react';
import { type HashAlgo, HASH_ALGOS, hashAll, hashAllText } from '@/lib/hash';
import { useStrings } from '@/i18n/useLang';

type Results = Record<HashAlgo, string> | null;

function HashRow({ algo, value }: { algo: HashAlgo; value: string }) {
  const t = useStrings();
  return (
    <Paper withBorder radius="md" p="sm" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-light)' }}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <div style={{ minWidth: 0 }}>
          <Text size="xs" fw={700} c="cyan">
            {algo}
          </Text>
          <Text ff="monospace" size="sm" style={{ wordBreak: 'break-all' }}>
            {value}
          </Text>
        </div>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t.ui.copied : t.ui.copy} withArrow>
              <ActionIcon variant="subtle" color="cyan" onClick={copy} aria-label={t.ui.copy}>
                {copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Paper>
  );
}

export function HashGenerator() {
  const t = useStrings();
  const [text, setText] = useState('');
  const [results, setResults] = useState<Results>(null);
  const [fileInfo, setFileInfo] = useState<string | null>(null);

  // Recompute text hashes whenever the input changes.
  useEffect(() => {
    let cancelled = false;
    hashAllText(text)
      .then((r) => {
        if (!cancelled) {
          setResults(r);
          setFileInfo(null);
        }
      })
      .catch(() => {
        if (!cancelled) setResults(null);
      });
    return () => {
      cancelled = true;
    };
  }, [text]);

  const onFile = async (file: File) => {
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const r = await hashAll(buf);
      setResults(r);
      setFileInfo(`${file.name} · ${(file.size / 1024).toFixed(1)} KB`);
    } catch {
      setResults(null);
    }
  };

  return (
    <Stack gap="lg">
      <Tabs defaultValue="text" variant="pills" color="cyan">
        <Tabs.List grow mb="md">
          <Tabs.Tab value="text" leftSection={<TextAaIcon size={18} />}>
            {t.ui.tabText}
          </Tabs.Tab>
          <Tabs.Tab value="file" leftSection={<FileArrowUpIcon size={18} />}>
            {t.ui.tabFile}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="text">
          <Textarea
            label={t.ui.textLabel}
            placeholder={t.ui.textPlaceholder}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            autosize
            minRows={3}
            maxRows={10}
          />
        </Tabs.Panel>

        <Tabs.Panel value="file">
          <Dropzone onDrop={(files) => files[0] && onFile(files[0])} multiple={false} radius="md">
            <Group justify="center" gap="sm" mih={120} style={{ pointerEvents: 'none' }}>
              <FileArrowUpIcon size={32} color="var(--primary)" />
              <div>
                <Text fw={600}>{t.ui.dropTitle}</Text>
                <Text size="sm" c="dimmed">
                  {t.ui.dropHint}
                </Text>
              </div>
            </Group>
          </Dropzone>
          {fileInfo && (
            <Text size="sm" c="dimmed" mt="xs">
              {fileInfo}
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>

      <Stack gap="sm">
        <Group gap="xs">
          <HashIcon size={18} color="var(--primary)" />
          <Text fw={600}>{t.ui.output}</Text>
        </Group>
        {results && HASH_ALGOS.map((algo) => <HashRow key={algo} algo={algo} value={results[algo]} />)}
      </Stack>
    </Stack>
  );
}
